/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncDirective, nothing, Part, PartType, render, TemplateResult } from '@rxdi/lit-html';

const VALUE_NOT_INITIALIZED = Symbol('valueNotInitialized');
export type Renderer = <T>(value: T) => TemplateResult;

export class LitRendererDirective extends AsyncDirective {
  element: unknown;

  private prevValue = VALUE_NOT_INITIALIZED;
  private renderer: Renderer;
  private host: object;

  constructor(part: Part) {
    super(part);

    if (part.type !== PartType.ELEMENT) {
      throw new Error(`\`${this.constructor.name}\` must be bound to an element.`);
    }
  }

  render(_renderer: Renderer, _value: any) {
    return nothing;
  }

  update(part: Part, [renderer, value]: [Renderer, any]) {
    if (!this.hasChanged(value)) {
      return nothing;
    }

    this.host = part.options?.host;
    this.element = part['element'];
    this.renderer = renderer;

    const firstRender = this.prevValue === VALUE_NOT_INITIALIZED;
    if (firstRender) {
      this.addRenderer();
    } else {
      this.runRenderer();
    }

    this.prevValue = Array.isArray(value) ? [...value] : value;

    return nothing;
  }

  reconnected() {
    this.addRenderer();
  }

  disconnected() {
    this.removeRenderer();
  }

  addRenderer() {
    throw new Error('The `addRenderer` method must be implemented.');
  }

  runRenderer() {
    throw new Error('The `runRenderer` method must be implemented.');
  }

  removeRenderer() {
    throw new Error('The `removeRenderer` method must be implemented.');
  }

  protected renderRenderer(container: HTMLElement, ...args: any[]) {
    const templateResult = this.renderer.call(this.host, ...args);
    render(templateResult, container, { host: this.host });
  }

  protected hasChanged(value: any) {
    if (Array.isArray(value)) {
      if (!Array.isArray(this.prevValue)) {
        return true;
      }

      if (this.prevValue.length !== value.length) {
        return true;
      }

      // For arrays, perform shallow dirty checking for each element.
      return value.some((v, i) => v !== this.prevValue[i]);
    }

    return this.prevValue !== value;
  }
}
